import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useAuth } from '@opendoor/auth-fe';
import { eero } from '@opendoor/bricks/theme/eero';

import { useT } from 'helpers/i18n';
import { useHasActiveBuyerOffers } from 'helpers/useBuyerOffers';

import { useObservability } from '../../helpers/observability';
import { Lead, useLeads } from '../../helpers/useLeads';
import { getSessionExtras } from '../api';

const MenuItem = styled.a`
  :hover {
    color: ${eero.colors.brand50};
  }
  position: relative;
  color: #525975;
`;

const screenXsMax = 768 - 1;
const screenXssMax = 480 - 1;

const PrimaryNavSubmenu = styled.ul<{ isExpanded: boolean }>`
  position: absolute;
  top: 75px;
  right: 25px;
  padding: 25px;
  background: #ffffff;
  border-radius: 3px;
  text-align: left;
  min-width: max-content;
  ${(props) =>
    !props.isExpanded && `visibility: hidden; opacity: 0;`} -webkit-transition: all ease 0.3s;
  transition: all ease 0.3s;
  box-shadow: -5px 0 40px rgba(0, 0, 0, 0.1);
  margin: 0 0 0 -50px;
  @media (max-width: 1201px) {
    right: 20px;
  }
  @media (max-width: 767px) {
    right: 10px;
    left: inherit;
  }
  @media (max-width: 479px) {
    margin: 0;
    width: 120px;
  }
  :before {
    content: '';
    position: absolute;
    top: -7px;
    right: 10px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #ffffff;
    z-index: 4;
  }
`;

const PrimaryNavItem = styled.li<{ height: string }>`
  position: relative;
  height: ${(props) => props.height};
  align-items: center;
  line-height: 25px;
  list-style: none;

  @media (max-width: ${screenXsMax}px) {
    margin-left: 12px;
    height: auto;
  }

  @media (max-width: ${screenXssMax}px) {
    margin-left: 6px;
    height: auto;
  }
`;

interface AccountMenuProps {
  isExpanded: boolean;
}

const AccountMenu = (props: AccountMenuProps) => {
  const { trackEvent } = useObservability();
  const { t } = useT();
  const { user: human, logout } = useAuth();
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(props.isExpanded);
  const hasLeads = leads && leads.length > 0;

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  const handleSignOutClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  return (
    <PrimaryNavSubmenu isExpanded={props.isExpanded}>
      {hasLeads &&
        leads.slice(0, 3).map((lead: Lead) => (
          <PrimaryNavItem height={'25px'} key={lead.id} className="primary-nav-submenu-item">
            <MenuItem href={lead.url}>
              {lead.address.street}
              {lead.readable_offer_state && ` (${lead.readable_offer_state})`}
            </MenuItem>
          </PrimaryNavItem>
        ))}
      {leads && leads.length > 3 && (
        <PrimaryNavItem height={'25px'} className="primary-nav-submenu-item">
          <MenuItem href="/properties">{t('homepage.account_menu.more', 'More')}</MenuItem>
        </PrimaryNavItem>
      )}
      {hasLeads && <hr />}
      {hasResaleDashboard && (
        <PrimaryNavItem height={'25px'} className="primary-nav-submenu-item">
          <MenuItem href="/closing-dashboard">
            {t('homepage.account_menu.closing_dash', 'Closing dash')}
          </MenuItem>
        </PrimaryNavItem>
      )}
      {hasResaleDashboard && <hr />}
      {hasActiveOffers && (
        <PrimaryNavItem height={'25px'} className="primary-nav-submenu-item">
          <MenuItem
            onClick={() =>
              trackEvent('cta-click', 'dashboard-link', undefined, {
                categoryOverride: 'homepage',
              })
            }
            href="/dashboard"
          >
            {t('homepage.account_menu.dashboard', 'Dashboard')}
          </MenuItem>
        </PrimaryNavItem>
      )}
      <PrimaryNavItem height={'25px'} className="primary-nav-submenu-item">
        <MenuItem
          // TODO: remove categoryOverride
          // observability is initialized with 'cosmos' as the category for all events
          onClick={() =>
            trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
          }
          href="/account"
        >
          {t('homepage.account_menu.account', 'Account')}
        </MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItem height={'25px'} className="primary-nav-submenu-item">
        <MenuItem href="#" onClick={handleSignOutClick}>
          {t('homepage.account_menu.sign_out', 'Sign out')}
        </MenuItem>
      </PrimaryNavItem>
    </PrimaryNavSubmenu>
  );
};

export default AccountMenu;
